import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import Tokens from '@uipath/apollo-core';
import React from 'react';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';

import MaestroFlowIllustration from './MaestroFlowIllustration';
import type { CarouselSlideProps } from './types/CarouselSlideProps';

const AVAILABILITY_LINKS = {
    vscode: 'https://marketplace.visualstudio.com/items?itemName=uipath.uipath-maestro',
    openVsx: 'https://open-vsx.org/extension/uipath/uipath-maestro',
};

/** Fades the illustration out on its left so it never sits hard against the copy. */
const ILLUSTRATION_FADE = 'linear-gradient(to right, transparent, black 45%)';
/** How far the illustration may reach back over the copy column, in px. Only its
 * faded-out left edge lands there, so nothing overlaps visibly. */
const ILLUSTRATION_OVERLAP = 120;

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            backgroundColor: theme.palette.semantic.colorBackgroundSecondary,
        },
        // A soft wash behind the copy, the same one the Maestro launch splash uses.
        glow: {
            position: 'absolute',
            left: '-96px',
            top: '50%',
            width: '640px',
            height: '420px',
            transform: 'translateY(-50%)',
            borderRadius: '50%',
            filter: 'blur(64px)',
            backgroundColor: alpha(theme.palette.semantic.colorPrimary, 0.12),
            pointerEvents: 'none',
        },
        // Nothing here truncates or hides: text wraps and the slide grows instead
        // (the carousel track uses a minimum height, see CarouselWrapper).
        content: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            maxWidth: '576px',
            minWidth: 0,
            padding: '32px',
        },
        eyebrow: {
            fontSize: '12px',
            lineHeight: '16px',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        title: {
            fontFamily: Tokens.FontFamily.FontTitle,
            fontSize: '24px',
            lineHeight: '32px',
            fontWeight: 'bold',
            letterSpacing: '-0.01em',
            color: theme.palette.semantic.colorForeground,
            marginTop: '8px',
        },
        description: {
            fontSize: '14px',
            lineHeight: '20px',
            color: theme.palette.semantic.colorForegroundDeEmp,
            marginTop: '8px',
        },
        actions: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            marginTop: '20px',
        },
        button: { height: '40px' },
        alsoIn: {
            marginTop: '12px',
            fontSize: '14px',
            lineHeight: '20px',
            color: theme.palette.semantic.colorForegroundDeEmp,
            '& a': {
                color: theme.palette.semantic.colorForegroundLink,
                textDecoration: 'none',
            },
            '& a:hover': { textDecoration: 'underline' },
        },
        // The illustration takes the space the copy leaves and may reach back
        // over it by `ILLUSTRATION_OVERLAP`: that part of it is masked to
        // transparent, so it buys width without ever colliding with the text.
        // Below the breakpoint there is no longer room for both and it goes,
        // the same width at which the Bottom Left/Right layouts drop theirs.
        illustration: {
            flex: '1 1 400px',
            maxWidth: '560px',
            minWidth: '280px',
            marginLeft: `-${ILLUSTRATION_OVERLAP}px`,
            alignSelf: 'center',
            WebkitMaskImage: ILLUSTRATION_FADE,
            maskImage: ILLUSTRATION_FADE,
        },
        '@media all and (max-width: 1150px)': { illustration: { display: 'none' } },
    }),
);

const externalLink = (href: string, tabIndex: number) => (chunks: React.ReactNode) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={tabIndex}>
        {chunks}
    </a>
);

/**
 * The Maestro launch slide: an eyebrow, the headline and lead, one CTA per
 * product with the "Also in…" links beneath, and the node illustration on the
 * right. A dedicated layout because the Contentful slide shape has no slot for
 * an eyebrow, a second CTA, or inline links.
 */
const MaestroFlowSlideLayout = ({
    title,
    description,
    buttonText,
    buttonRedirectUrl,
    secondaryButtonText,
    secondaryButtonRedirectUrl,
    idx,
    currSlide,
}: CarouselSlideProps) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const tabIndex = idx === currSlide ? 0 : -1;

    const openInNewTab = (url?: string) => () => {
        window.open(url, '_blank')?.focus();
    };

    return (
        <div
            className={classes.root}
            data-cy={`carousel-slide-${idx}`}>
            <div className={classes.glow} />

            <div className={classes.content}>
                <div
                    className={classes.eyebrow}
                    data-cy={`maestro-flow-eyebrow-${idx}`}>
                    {translate({ id: 'CLIENT_CAROUSEL_MAESTRO_FLOW_EYEBROW' })}
                </div>
                <div
                    className={classes.title}
                    role="heading"
                    aria-level={1}
                    data-cy={`title-slide-${idx}`}>
                    {title}
                </div>
                <div
                    className={classes.description}
                    data-cy={`description-slide-${idx}`}>
                    {description}
                </div>

                <div
                    className={classes.actions}
                    data-cy={`button-slide-${idx}`}>
                    <Button
                        className={classes.button}
                        tabIndex={tabIndex}
                        variant="contained"
                        disableElevation
                        color="secondary"
                        data-cy={`carousel-slide-button-${idx}`}
                        onClick={openInNewTab(buttonRedirectUrl)}
                        style={{ fontSize: '14px' }}
                    >
                        {buttonText}
                    </Button>
                    <Button
                        className={classes.button}
                        tabIndex={tabIndex}
                        variant="contained"
                        disableElevation
                        color="secondary"
                        data-cy={`carousel-slide-secondary-button-${idx}`}
                        onClick={openInNewTab(secondaryButtonRedirectUrl)}
                        style={{ fontSize: '14px' }}
                    >
                        {secondaryButtonText}
                    </Button>
                </div>

                <div
                    className={classes.alsoIn}
                    data-cy={`maestro-flow-also-in-${idx}`}>
                    <FormattedMessage
                        id="CLIENT_CAROUSEL_MAESTRO_FLOW_ALSO_IN"
                        values={{
                            vscode: externalLink(AVAILABILITY_LINKS.vscode, tabIndex),
                            openvsx: externalLink(AVAILABILITY_LINKS.openVsx, tabIndex),
                        }}
                    />
                </div>
            </div>

            <MaestroFlowIllustration
                className={classes.illustration}
                data-cy={`maestro-flow-illustration-${idx}`}
            />
        </div>
    );
};

export default MaestroFlowSlideLayout;
